import FileActions from "components/FileDownloadButtons";
import { useTranslation } from "contexts/i18nContext";
import { useHasRole } from "contexts/AuthContext";
import { useState } from "react";
import { Complaints, CourtAppeal } from "types";
import {
  ACCUSED_COMPLAINT_STATUS,
  COURT_APPEAL_STATUS,
} from "utils/constantValues";
import { DateView } from "utils/helper";
import { ROLES } from "utils/roles";
// import AccusedSummonConfirmationModal from "./AccusedSummonConfirmationModal";
// import AddSummonResponse from "./AddSummonResponse";
import ConfirmationModal from "components/ConfirmationModal";
import { useToast } from "contexts/ToastContext";
import { editCourtAppeal } from "services/prosecutorDecipilineService";
import AddCourtAppealResponse from "./AddCourtAppealResponse";
import CourtSummonConfirmationModal from "./CourtSummonConfirmationModal";
import DocumentViewerModal from "components/DocumentViewer";

interface SummonProps {
  summonData: CourtAppeal;
  compliantData: Complaints;
  onSummonRefresh: any;
  onComplaintsRefresh: any;
}

export default function CourtSummonCard({
  summonData,
  compliantData,
  onSummonRefresh,
  onComplaintsRefresh,
}: SummonProps) {
  const { t } = useTranslation();
  const canCoordinatorConfirmSummon = useHasRole([
    ROLES.INSPECTION_COORDINATOR,
    ROLES.INSPECTION_DIRECTOR,
  ]);
  const canAccusedConfirmSummon = useHasRole([ROLES.INSPECTION_PROSECUTOR]);
  const canUpdateHearingDate = useHasRole([
    ROLES.REGIONAL_COUNCIL_OFFICE_HEAD,
    ROLES.REGIONAL_COUNCIL_OFFICE_WRITER,
    ROLES.ZONAL_COUNCIL_OFFICE_HEAD,
    ROLES.ZONAL_COUNCIL_OFFICE_WRITER,
  ]);
  const canApproveAccusedSummon = useHasRole([
    ROLES.INSPECTION_COORDINATOR,
    ROLES.INSPECTION_DIRECTOR,
  ]);

  const { showToast } = useToast();

  const [loading, setLoading] = useState(false);
  const [showResponseModal, setShowResponseModal] = useState<boolean>(false);
  const [coordinatorConfirmationModal, setCoordinatorConfirmationModal] =
    useState<boolean>(false);
  const [showConfirmationModal, setShowConfirmationModal] =
    useState<boolean>(false);
  const [isEditingCourtHearingDate, setIsEditingCourtHearingDate] =
    useState(false);
  const [courtHearingDateDraft, setCourtHearingDateDraft] = useState("");
  const [isUpdatingCourtHearingDate, setIsUpdatingCourtHearingDate] =
    useState(false);

  const toDateInputValue = (value?: string | null) => {
    if (!value) return "";
    if (/^\d{4}-\d{2}-\d{2}/.test(value)) return value.slice(0, 10);

    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return "";
    return parsed.toISOString().slice(0, 10);
  };

  const startCourtHearingDateEdit = () => {
    setCourtHearingDateDraft(toDateInputValue(summonData?.court_hearing_date));
    setIsEditingCourtHearingDate(true);
  };

  const cancelCourtHearingDateEdit = () => {
    setIsEditingCourtHearingDate(false);
    setCourtHearingDateDraft("");
  };

  const handleUpdateCourtHearingDate = async () => {
    if (!summonData?.id) {
      showToast(t("courtSummonNotFound"), "error");
      return;
    }

    const currentDate = toDateInputValue(summonData?.court_hearing_date);
    if (!courtHearingDateDraft || courtHearingDateDraft === currentDate) {
      showToast("No date change detected", "error");
      return;
    }

    setIsUpdatingCourtHearingDate(true);
    try {
      await editCourtAppeal(
        {
          court_hearing_date: courtHearingDateDraft,
        } as any,
        summonData.id,
        false,
      );

      await Promise.resolve(onSummonRefresh?.());
      cancelCourtHearingDateEdit();
      showToast("Court hearing date updated", "success");
    } catch (err) {
      showToast("Failed to update court hearing date", "error");
    } finally {
      setIsUpdatingCourtHearingDate(false);
    }
  };

  const handleApproveClick = async () => {
    if (!summonData) {
      showToast(t("courtSummonNotFound"), "error");
      return;
    }
    setLoading(true);

    const data = new FormData();

    data.append("status", COURT_APPEAL_STATUS.APPROVED);

    const summon_id = summonData?.id || "";
    const isFormData = true;

    try {
      await editCourtAppeal(data, summon_id, isFormData);

      onSummonRefresh();
      setShowConfirmationModal(false);
      showToast(t("successfullyConfirmed"), "success");
    } catch (err: any) {
      console.error("Submission error:", err);
      showToast(t("submissionError"), "error");
    } finally {
      setLoading(false);
    }
  };

  if (!summonData) return null;

  return (
    <div className="mb-8">
      {summonData && (
        <div
          className={
            "mx-auto bg-white border-black border-8 rounded-xl  overflow-hidden mb-4"
          }
        >
          <div className="p-5">
            <h1 className="text-1xl font-bold text-gray-800 mb-3">
              {t("courtAppealSummon")}
            </h1>

            {summonData?.assigned_prosecutor && (
              <div className="relative border border-gray-400 bg-white rounded-md p-2 my-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <img
                      src={require("../../../images/avatar.png")}
                      alt="avatar"
                      className="w-8 h-8 rounded-full"
                    />
                    <div>
                      <p className="font-medium text-sm">
                        {summonData?.assigned_prosecutor || "-"}
                      </p>
                      <p className="text-xs text-gray-500">{t("assignedTo")}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="">
              <hr />

              {summonData?.court_hearing_date && (
                <div className="w-full flex flex-wrap items-center justify-between gap-4 my-2">
                  <p className="block text-gray-600">{t("courtHearingDate")}</p>
                  <div className="flex items-center gap-3">
                    <p className="block text-black text-sm font-medium">
                      {summonData?.court_hearing_date
                        ? DateView(summonData.court_hearing_date)
                        : "-"}
                    </p>
                    {canUpdateHearingDate && (
                      <button
                        type="button"
                        onClick={startCourtHearingDateEdit}
                        className="text-xs font-medium text-red-700 hover:text-red-800 underline"
                      >
                        {t("updateDate")}
                      </button>
                    )}
                  </div>
                </div>
              )}

              {isEditingCourtHearingDate && (
                <div className="my-3 rounded-md border border-gray-200 bg-gray-50 p-3 space-y-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      {t("courtHearingDate")}
                    </label>
                    <input
                      type="date"
                      value={courtHearingDateDraft}
                      onChange={(e) => setCourtHearingDateDraft(e.target.value)}
                      className="w-full md:w-64 rounded border border-gray-300 px-2 py-1.5 text-sm"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      disabled={isUpdatingCourtHearingDate}
                      onClick={handleUpdateCourtHearingDate}
                      className="rounded bg-red-700 px-3 py-1.5 text-xs font-medium text-white hover:bg-red-800 disabled:opacity-60"
                    >
                      {isUpdatingCourtHearingDate ? "Updating..." : "Save Date"}
                    </button>
                    <button
                      type="button"
                      disabled={isUpdatingCourtHearingDate}
                      onClick={cancelCourtHearingDateEdit}
                      className="rounded border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-100"
                    >
                      {t("cancel")}
                    </button>
                  </div>
                </div>
              )}

              <hr />

              {summonData?.court_name && (
                <div className="w-full flex flex-wrap items-center justify-between gap-4 my-2">
                  <p className="block text-gray-600">{t("courtName")}</p>
                  <p className="block text-black text-sm font-medium">
                    {summonData?.court_name}
                  </p>
                </div>
              )}
              <hr />

              {summonData?.court_number && (
                <div className="w-full flex flex-wrap items-center justify-between gap-4 my-2">
                  <p className="block text-gray-600">{t("courtNumber")}</p>
                  <p className="block text-black text-sm font-medium">
                    {summonData?.court_number}
                  </p>
                </div>
              )}
              <hr />

              {summonData?.hearing_court && (
                <div className="w-full flex flex-wrap items-center justify-between gap-4 my-2">
                  <p className="block text-gray-600">{t("hearingCourt")}</p>
                  <p className="block text-black text-sm font-medium">
                    {summonData?.hearing_court}
                  </p>
                </div>
              )}
              <hr />

              {summonData?.court_order_summon && (
                <div className="w-full flex flex-wrap items-center justify-between gap-4 my-2">
                  <p className="block text-gray-600">{t("courtOrder")}</p>
                  <p className="block text-black text-sm font-medium">
                    {summonData?.court_order_summon}
                  </p>
                </div>
              )}
              <hr />

              {summonData?.registration_number && (
                <div className="w-full flex flex-wrap items-center justify-between gap-4 my-2">
                  <p className="block text-gray-600">
                    {t("registrationNumber")}
                  </p>
                  <p className="block text-black text-sm font-medium">
                    {summonData?.registration_number}
                  </p>
                </div>
              )}
              <hr />

              {summonData?.registration_date && (
                <div className="w-full flex flex-wrap items-center justify-between gap-4 my-2">
                  <p className="block text-gray-600">{t("registrationDate")}</p>
                  <p className="block text-black text-sm font-medium">
                    {summonData?.registration_date}
                  </p>
                </div>
              )}
              <hr />
            </div>

            {/* Summary Desc */}
            {/* <div className="my-4">
              <h1 className="text-1xl font-bold text-gray-800 mb-1">Summary</h1>
              <p className="text-gray-600 leading-relaxed">
                {summonData?.summary || "-"}
              </p>
            </div> */}

            {/* Files */}
            {summonData?.court_summon_details && (
              <div className="flex justify-between items-center p-2 bg-gray-50 rounded-md my-3">
                <div className="flex items-center gap-2">
                  <div className="bg-[#5e3b1f] text-white font-bold text-xs px-2 py-1 rounded">
                    PDF
                  </div>
                  <div>
                    <p className="text-sm font-medium">
                      {summonData?.court_summon_file_details?.file_name ||
                        t("summonDoc")}
                    </p>
                    {/* <p className="text-xs text-gray-500">11.3MB</p> */}
                  </div>
                </div>

                <FileActions
                  fileUrl={summonData?.court_summon_file_details?.url || ""}
                />
              </div>
            )}

            {/* Response Summary Desc */}
            {summonData?.response_summary && (
              <>
                <hr />
                <div className="my-4">
                  <h1 className="text-1xl font-bold text-gray-800 mb-1">
                    {t("responseSummary")}
                  </h1>
                  <p className="text-gray-600 leading-relaxed">
                    {summonData?.response_summary || "-"}
                  </p>
                </div>
              </>
            )}

            {summonData?.documents && (
              <div className="mt-2">
                <DocumentViewerModal
                  documents={(summonData?.documents || []) as any}
                  baseUrl={`${process.env.REACT_APP_API_BASE_URL}prosecutor-decipiline/litigation-counciloffice-documents/document`}
                />
              </div>
            )}
          </div>
        </div>
      )}

      {canCoordinatorConfirmSummon &&
        summonData?.status === COURT_APPEAL_STATUS.FORWARDED_TO_COORDINATOR &&
        !summonData?.assigned_prosecutor && (
          <div className="text-right">
            <button
              className="bg-black text-white px-14 py-2 rounded-md hover:bg-gray-800"
              type="submit"
              onClick={() => setCoordinatorConfirmationModal(true)}
            >
              {t("assignProsecutor")}
            </button>
          </div>
        )}

      {canAccusedConfirmSummon &&
        summonData?.status === COURT_APPEAL_STATUS.ASSIGNED_TO_PROSECUTOR &&
        !summonData?.response_summary && (
          <div className="text-right">
            <button
              className="bg-black text-white px-14 py-2 rounded-md hover:bg-gray-800"
              type="submit"
              onClick={() => setShowResponseModal(true)}
            >
              {t("addResponse")}
            </button>
          </div>
        )}

      {canApproveAccusedSummon &&
        summonData?.status !== ACCUSED_COMPLAINT_STATUS.APPROVED &&
        (summonData?.response_summary || summonData?.response) && (
          <div className="text-right">
            <button
              className="bg-black text-white px-14 py-2 rounded-md hover:bg-gray-800"
              type="submit"
              onClick={() => setShowConfirmationModal(true)}
            >
              {t("approveButton")}
            </button>
          </div>
        )}

      {summonData && compliantData && (
        <CourtSummonConfirmationModal
          isOpen={coordinatorConfirmationModal}
          setIsOpen={setCoordinatorConfirmationModal}
          complaintData={compliantData}
          summonData={summonData}
          onRefresh={onSummonRefresh}
          onComplaintsRefresh={onComplaintsRefresh}
        />
      )}

      {summonData && (
        <AddCourtAppealResponse
          isOpen={showResponseModal}
          setIsOpen={setShowResponseModal}
          summonData={summonData}
          onRefresh={onSummonRefresh}
        />
      )}

      <ConfirmationModal
        isOpen={showConfirmationModal}
        onCancel={() => setShowConfirmationModal(false)}
        onConfirm={handleApproveClick}
        isLoading={loading}
      />
    </div>
  );
}
